import { findRow, formatPrice } from "@/data/pricing";
import { createFileRoute } from "@tanstack/react-router";
import {
  breadcrumbSchema,
  canonicalLinks,
  faqPageSchema,
  jsonLd,
  ogImage,
  ogUrl,
  absoluteUrl,
  serviceSchema,
  webPageSchema,
} from "@/lib/seo";
import { LocationPage } from "@/components/site/LocationPage";

const PATH = "/fractional-coo-australia";
const TITLE = "Fractional COO and GM Australia, On Site or Remote | Ajility";
const DESCRIPTION =
  "Fractional COO services for Australian businesses of $3M to $30M. On site in Sydney, Melbourne, Perth, Newcastle and the Central Coast, remote elsewhere.";

const SERVICE_ID = `${absoluteUrl(PATH)}#service`;

const faqs = [
  {
    q: "What is a fractional COO?",
    a: "A fractional COO is a senior operator who runs the operating side of a business part time: chairing the leadership meeting, holding people to their priorities, building the scorecard and margin visibility, and fixing the processes that keep failing. The work is delivery inside the business, not a report.",
  },
  {
    q: "How much does a fractional COO cost in Australia?",
    a: `Market rates vary widely and are rarely published. Ajility publishes its prices: the Operating Diagnostic is ${formatPrice(findRow("Operating Diagnostic"))}, the Full Diagnostic and 90-Day Roadmap is ${formatPrice(findRow("Full Diagnostic and 90-Day Roadmap"))}, and composed retainers for businesses under $15M start ${formatPrice(findRow("Fortnightly rhythm base"))}. Every price is a from price based on size and on-site share.`,
  },
  {
    q: "Which cities does Ajility work in?",
    a: "On site in Sydney, Melbourne, Perth, Newcastle and the Central Coast, and remotely with founder-led businesses anywhere in Australia, including Brisbane, Adelaide, Canberra, the Gold Coast and regional centres. The same published prices apply everywhere.",
  },
  {
    q: "Fractional COO or full-time COO?",
    a: "A full-time COO in Australia costs a senior salary plus on-costs and suits a business with a full week of COO work. Most businesses between $3M and $30M have real operating problems but not a full week of them. A fractional COO gives senior judgement sized to the work, a fast start and quarterly exit points.",
  },
  {
    q: "How long does a fractional COO engagement last?",
    a: "Engagements commonly run six to eighteen months, but the right length is however long it takes your team to run the rhythm without help. Ajility commits one quarter at a time and decides at each 90-day boundary whether to continue, pause or close.",
  },
  {
    q: "Is a fractional COO an employee or a contractor?",
    a: "Fractional COOs are usually engaged as a business under a services agreement rather than employed, so there is no payroll, super, leave or recruitment fee. Ajility engages through AJILITY PTY LTD on a signed agreement, with fees quoted plus GST.",
  },
];

export const Route = createFileRoute("/fractional-coo-australia")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Fractional COO in Australia for founder-led businesses" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Fractional COO in Australia for founder-led businesses" },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("home", "Fractional COO services across Australia from Ajility"),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Fractional COO Australia", path: PATH }])),
      jsonLd(
        webPageSchema({
          image: "home",
          path: PATH,
          name: TITLE,
          description: DESCRIPTION,
          mainEntityId: SERVICE_ID,
        }),
      ),
      jsonLd({
        ...serviceSchema({
          name: "Fractional COO Australia",
          description: DESCRIPTION,
          path: PATH,
          slug: "fractional-coo-australia",
          serviceType: "Fractional COO",
          offerId: ["operational-diagnostic", "full-diagnostic-roadmap", "fortnightly-rhythm-base"],
        }),
        areaServed: [
          { "@type": "Country", name: "Australia" },
          { "@type": "City", name: "Sydney" },
          { "@type": "City", name: "Central Coast" },
          { "@type": "City", name: "Newcastle" },
          { "@type": "City", name: "Melbourne" },
          { "@type": "City", name: "Perth" },
        ],
      }),
      jsonLd(faqPageSchema(PATH, faqs, SERVICE_ID)),
    ],
  }),
  component: AustraliaPage,
});

function AustraliaPage() {
  return (
    <LocationPage
      city="Australia"
      region="on site and remote"
      heading="Fractional COO in Australia"
      lead="Hands-on COO leadership for founder-led Australian businesses of $3M to $30M. On site in Sydney, Melbourne, Perth, Newcastle and the Central Coast, remote everywhere else, with published prices and no day rates. Most Australian businesses call this seat the general manager; a fractional GM and a fractional COO are the same role."
      intro={[
        "A fractional COO in Australia is a senior operator who holds the operating seat in your business part time, usually one day a week or a fortnight, inside the leadership meeting and accountable for delivery rather than advice. It suits the business that has outgrown informal management but does not yet have a full week of COO work, which in Australia is most founder-led businesses between $3M and $30M in revenue with 15 to 150 staff.",
        "Ajility is a fractional COO firm based in Sydney and working across Australia. It works on site in Sydney, Melbourne, Perth, Newcastle and the Central Coast, and remotely with founder-led businesses anywhere else in the country. Every engagement is led by the principal, Adam Ayoub, who has held general manager and COO seats for more than fifteen years across contracting, transport, healthcare and services businesses.",
        "Few Australian fractional COO providers publish their prices, and most price by the day. Ajility publishes every price, works to named deliverables rather than hours, and starts most engagements with a fixed-price diagnostic so you know what is wrong before you commit to a retainer.",
      ]}
      industriesHeading="Where Australian founders use a fractional COO"
      industries={[
        {
          name: "Construction and trade contracting",
          note: "Subcontractors and specialist trades in every capital and regional centre. Variations, retentions and security of payment timeframes decide whether a profitable job is a profitable year. Weekly job costing and cost to complete are the first installs.",
        },
        {
          name: "Professional and technical services",
          note: "Consultancies, engineering firms, agencies and advisory practices where the product is billable time. Utilisation, scope creep and write-offs at invoice are where margin leaks. Job-level margin and a scope-change discipline recover it.",
        },
        {
          name: "Manufacturing, wholesale and distribution",
          note: "Factories and distributors from Melbourne's south east to western Sydney. Workshop rates that have not moved with costs, cost to serve by customer and stock, fleet and labour managed by three different people with three different numbers.",
        },
        {
          name: "Transport and logistics",
          note: "Fleet operators and freight businesses where margin is won or lost at lane and job level. A short weekly scorecard with one owner per number usually settles arguments that have run for years.",
        },
        {
          name: "Healthcare and allied services",
          note: "Clinician-founded groups adding sites. Clinical quality is rarely the constraint. The founder is still the practice manager, the recruiter and the roster, and the third site exposes it.",
        },
        {
          name: "Mining services and regional contracting",
          note: "Contractors in the Pilbara, the Hunter and regional Queensland where mobilisation, unclaimed variations and slow certification fund the client's work. A 13 week cash forecast tied to the claim schedule shows it early.",
        },
      ]}
      sections={[
        {
          heading: "How a fractional COO engagement runs across Australia",
          paragraphs: [
            "Engagements in Sydney, Melbourne, Perth, Newcastle and the Central Coast run on site. A composed retainer typically runs one day a week or a fortnight inside the business, with the weekly leadership session held in person and the rest of the week available by phone for decisions that cannot wait.",
            "Outside those cities the pattern shifts to a fortnightly on-site day with structured remote weeks either side, or to a fully remote rhythm for businesses that already run their leadership meeting online. Documentation, scorecard build, margin analysis and one-to-one coaching of the leadership team run between sessions wherever the business is. Travel time is absorbed in the price; for interstate work, flights and accommodation are arranged by the client or charged at cost.",
            "Every engagement is committed one quarter at a time. At each 90-day boundary the business decides whether to continue, pause or close, and handover is a written deliverable rather than a hope.",
          ],
        },
        {
          heading: "What a fractional COO costs in Australia",
          paragraphs: [
            `Ajility prices named deliverables, not time. The Operating Diagnostic is ${formatPrice(findRow("Operating Diagnostic"))}. The Full Diagnostic and 90-Day Roadmap is ${formatPrice(findRow("Full Diagnostic and 90-Day Roadmap"))} for one entity and one site. Composed retainers for businesses under $15M start ${formatPrice(findRow("Fortnightly rhythm base"))}, and the weekly rhythm base is ${formatPrice(findRow("Weekly rhythm base"))}, with actively led service lines added on top. The 90-Day Operating System Install, which bundles the diagnosis and the install, is ${formatPrice(findRow("90-Day Operating System Install"))}. Every price is a from price based on the size of the business and how much of the work is on site.`,
            "There is no metropolitan loading and no regional surcharge. The full ladder, what each item includes and the 30-day credit from the entry diagnostic are on the packages page, and the market comparison is in the article on what a fractional COO costs in Australia.",
          ],
        },
        {
          heading: "What tends to be wrong in a founder-led Australian business",
          paragraphs: [
            "The first thing is almost always decision routing. Everything climbs back to the founder because there are no thresholds and no owners, so the founder's week is spent approving and the team's week is spent waiting.",
            "The second is margin visibility. Most businesses know revenue to the dollar and job, customer or site margin to the vibe. Once that view exists the pattern is nearly always the same: a few clients or jobs carry the business and another group is quietly funded by them.",
            "The third is cadence. Without a fixed weekly leadership session, a monthly commercial review and a hard quarterly reset, priorities set in January are unrecognisable by March. Installing that rhythm is the Ajility Operating System and Governance service, and it is the same install in Perth as it is in Parramatta.",
          ],
        },
      ]}
      faqHeading="Fractional COO questions from Australian founders"
      faqs={faqs}
      nextHeading="Where Australian founders usually go next"
      relatedLinks={[
        { to: "/fractional-coo-sydney", label: "Fractional COO Sydney", note: "On site weekly across the CBD, Parramatta, Norwest and western Sydney." },
        { to: "/fractional-coo-melbourne", label: "Fractional COO Melbourne", note: "Manufacturing, wholesale and services businesses across greater Melbourne and Geelong." },
        { to: "/fractional-coo-perth", label: "Fractional COO Perth", note: "Contracting, mining services and project businesses in Perth and the Pilbara." },
        { to: "/fractional-coo-newcastle", label: "Fractional COO Newcastle", note: "Hunter contractors, fabricators and logistics businesses." },
        { to: "/fractional-coo-central-coast", label: "Fractional COO Central Coast", note: "Coast businesses serving Sydney and the Hunter." },
        { to: "/packages", label: "Published pricing", note: "Every diagnostic, install and retainer with its from price and what it includes." },
      ]}
      cta={{
        heading: "Talk to an operator, wherever you are in Australia",
        body: "A thirty-minute fit call, not a pitch. It ends with a one-sentence view of where your constraint sits and an agreed next step with a date on it, whether the engagement runs on site or remotely.",
        linkLabel: "Book a fit call",
      }}
    />
  );
}